import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as customerInitialState } from '~redux/slices/gold';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { BranchStatusEnum, FundTypeEnum } from '~utils/enums';
import * as Styles from './_reportsSearchAndFilterbar.module.scss';
import { ReportGoldInventoryReceivedListingFilters } from '~types/redux/state';

const InventoryReceivedSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();

    const filters = useAppSelector(state => state.gold.goldInventoryReport.inventoryReceived.filters);
    const initialFilters = customerInitialState.goldInventoryReport.inventoryReceived.filters;
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const branchesList = useAppSelector(state => state.branch.allBranches);

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active);
    const productList = useAppSelector((state) => state.gold.allProducts);

    const setFilters = (inputFilters: ReportGoldInventoryReceivedListingFilters) => dispatch(combinedActions.goldSetInventoryReceivedListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.goldGetGoldInventoryReportReceivedListingAttempt());

    useEffect(() => {
        if (branchOptions.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchName: branchOptions.find((branch) => branch.branchCode === branchCode)?.branchName || '',
                branchCode: branchCode || '',
            });
        }
    }, branchesList);

    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchName: !allBranchAccess ? (branchOptions.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
            branchCode: !allBranchAccess ? branchCode : '',
        });
        dispatch(combinedActions.goldGetGoldInventoryReportReceivedListingAttempt());
    };

    const massagedBranchesListOption = () => {
        if (!branchOptions.length) return [];

        return branchOptions.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Choose branch to view:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                    branchCode: branchOptions.find((branch) => branch.branchName === event.target.value)?.branchCode || '',
                }),
                optionSelected: !!branchOptions.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Choose fund type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Islamic',
                        value: FundTypeEnum.Islamic,
                    },
                    {
                        label: 'Conventional',
                        value: FundTypeEnum.Conventional,
                    },
                ],
                value: filters.fundType,
                setValue: (event) => setFilters({ ...filters, fundType: event.target.value }),
                optionSelected: !!FundTypeEnum[filters.fundType as keyof typeof FundTypeEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
        {
            label: 'Product Name:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedProductsListOption(),
                value: filters.productName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    productName: event.target.value,
                    productId: productList.find((product) => product.productName === event.target.value)?.productId || 0,
                }),
                optionSelected: !!productList.find((product) => product.productName === filters.productName),
                noOptionsMessage: 'No product found',
                className: Styles.input,
            },

        },
        {
            label: 'Serial Number:',
            type: 'input' as const,
            inputParams: {
                value: filters.serialNo || '',
                setValue: (event) => setFilters({ ...filters, serialNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default InventoryReceivedSearchAndFilterBar;
